import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import mesaLogo from '@/assets/mesa_logo_light_circle.webp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPasswordServerFn } from '@/server/functions/auth';

const searchSchema = z.object({
  token: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/(auth)/reset-password')({
  validateSearch: searchSchema,
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const { token, error: tokenError } = Route.useSearch();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(
    tokenError === 'INVALID_TOKEN' ? 'This reset link is invalid or has expired.' : null
  );
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Missing reset token');
      return;
    }

    setLoading(true);

    try {
      await resetPasswordServerFn({ data: { newPassword: password, token } });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-2xl font-semibold text-mesa-text tracking-tight mb-4">Password reset!</h1>
            <p className="text-mesa-muted mb-6">Your password has been successfully reset.</p>
            <Button onClick={() => navigate({ to: '/sign-in' })} className="w-full">
              Sign in
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  if (!token || tokenError) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-2xl font-semibold text-mesa-text tracking-tight mb-4">Invalid link</h1>
            <p className="text-mesa-muted mb-6">
              {error || 'This password reset link is invalid or has expired. Please request a new one.'}
            </p>
            <Button onClick={() => navigate({ to: '/sign-in' })} className="w-full">
              Back to sign in
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="h-2 bg-mesa-green" />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8">
          <div className="text-center mb-8">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-2xl font-semibold text-mesa-text tracking-tight">Set new password</h1>
            <p className="mt-2 text-sm text-mesa-muted">Enter your new password below.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="border border-mesa-red/30 bg-mesa-red/10 p-3 text-sm text-mesa-red">{error}</div>}

            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter new password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>

            <div className="text-center text-sm text-mesa-muted">
              <button
                type="button"
                onClick={() => navigate({ to: '/sign-in' })}
                className="text-mesa-green hover:underline"
              >
                Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="h-2 bg-mesa-green" />
    </div>
  );
}
